import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Hexagon, Phone, Globe, Lock, User, ArrowRight, CheckCircle2, MapPin, MessageCircle } from 'lucide-react';

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (password !== confirmPassword) {
            setError("Şifreler eşleşmiyor");
            setLoading(false);
            return;
        }
        
        const res = await register(username, password);
        if (res.success) {
            navigate('/login');
        } else {
            setError(res.error);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4 font-sans">
            <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
                
                {/* Left Side - Hero / Branding */}
                <div className="md:w-1/2 bg-gradient-to-br from-amber-500 to-amber-600 p-8 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10">
                        <Hexagon size={200} className="absolute -top-10 -left-10 text-white animate-pulse" />
                        <Hexagon size={100} className="absolute bottom-10 right-10 text-white" />
                    </div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                                <Hexagon size={32} fill="white" className="text-amber-600" />
                            </div>
                            <h1 className="text-2xl font-bold tracking-wide">KovanTakip</h1>
                        </div>
                        
                        <div className="space-y-4 mb-8">
                            <h2 className="text-3xl font-bold leading-tight">Aramıza Katılın</h2>
                            <p className="text-amber-100 text-sm">Profesyonel arıcılık yönetim sistemine dahil olun.</p>
                            <div className="bg-white/10 p-3 rounded-lg border border-white/20 backdrop-blur-sm">
                                <p className="font-bold text-amber-300 flex items-center gap-2">
                                    <CheckCircle2 size={18} /> Ücretsiz Başlangıç Paketi
                                </p>
                                <p className="text-xs text-white mt-1 pl-6">
                                    Kayıt olan herkese <strong>5 Kovanlık</strong> kullanım hakkı hediye! Kredi kartı gerekmez.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2 text-sm text-amber-50 font-medium">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-green-300" />
                                <span>Kolay Kayıt</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-green-300" />
                                <span>Hızlı Başlangıç</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 mt-8 pt-6 border-t border-white/20">
                        <p className="text-xs uppercase tracking-wider opacity-70 mb-2">Geliştirici & Destek</p>
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            3B Bilgisayar Yazılım
                        </h3>
                        <div className="mt-3 space-y-2 text-sm text-amber-100">
                            <div className="flex items-center gap-2">
                                <Phone size={14} className="shrink-0" /> 0505 223 26 57
                            </div>
                            <div className="flex items-start gap-2">
                                <MapPin size={14} className="mt-1 shrink-0" />
                                <span>Yeni Mahalle Milli Egemenlik Caddesi No 17/C Araç/Kastamonu</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Globe size={14} className="shrink-0" /> www.3bbilgisayar.com
                            </div>
                            <div className="pt-2">
                                <a 
                                    href="https://wa.me/905052232657?text=Merhaba,%20KovanTakip%20hakkında%20bilgi%20almak%20istiyorum." 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-green-300 hover:text-green-100 font-bold transition"
                                >
                                    <MessageCircle size={16} /> WhatsApp Destek Hattı
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Register Form */}
                <div className="md:w-1/2 p-8 md:p-12 bg-white flex flex-col justify-center">
                    <div className="text-center md:text-left mb-8">
                        <h2 className="text-2xl font-bold text-gray-800">Kayıt Ol</h2>
                        <p className="text-gray-500 text-sm mt-1">Yeni bir hesap oluşturun.</p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition sm:text-sm"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Şifre</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition sm:text-sm"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Şifre Tekrar</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition sm:text-sm"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 transition"
                        >
                            {loading ? 'Kaydediliyor...' : 'Kayıt Ol'}
                            {!loading && <ArrowRight size={18} className="ml-2" />}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            Zaten hesabınız var mı? <Link to="/login" className="text-amber-600 font-bold hover:underline">Giriş Yap</Link>
                        </p>
                    </div>
                </div>
            </div>
            
            <div className="fixed bottom-4 text-center w-full text-xs text-gray-400 pointer-events-none">
                &copy; {new Date().getFullYear()} 3B Bilgisayar Yazılım. Tüm hakları saklıdır.
            </div>
        </div>
    );
}
